import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import {
  Package, Plus, Search, Filter, X, Save, Truck, FileText,
  Calendar, DollarSign, Hash, ChevronDown, Trash2, Download,
  Check, AlertTriangle, ClipboardList, ArrowLeft, Edit2, PackageCheck
} from 'lucide-react';
import { useTheme } from './ThemeContext';
import { useModal } from './AppModal';
import jsPDF from 'jspdf';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'partially_received', label: 'Partial' },
  { value: 'received', label: 'Received' },
  { value: 'cancelled', label: 'Cancelled' }
];

const STATUS_COLORS = {
  draft: { bg: '#6b7280', text: '#fff' },
  sent: { bg: '#3b82f6', text: '#fff' },
  partially_received: { bg: '#f59e0b', text: '#000' },
  received: { bg: '#10b981', text: '#fff' },
  cancelled: { bg: '#ef4444', text: '#fff' }
};

function generatePONumber() {
  return 'PO-' + Date.now().toString(36).toUpperCase();
}

function formatCurrency(val) {
  const num = parseFloat(val) || 0;
  return '$' + num.toFixed(2);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PurchaseOrdersPage({ companyId }) {
  const { colors } = useTheme();
  const { showConfirm, showSuccess, showError } = useModal();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // List state
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [saving, setSaving] = useState(false);

  // PO form
  const [poForm, setPOForm] = useState({
    vendor_id: '',
    expected_date: '',
    notes: '',
    tax: 0
  });
  const [poItems, setPOItems] = useState([]);

  // Receive flow
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receivingPO, setReceivingPO] = useState(null);
  const [receiveItems, setReceiveItems] = useState([]);
  const [receiving, setReceiving] = useState(false);

  // Inventory items for picker
  const [inventoryItems, setInventoryItems] = useState([]);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [itemSearchTerm, setItemSearchTerm] = useState('');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [posRes, vendorsRes, invRes] = await Promise.all([
        supabaseData.from('purchase_orders')
          .select('*, purchase_order_items(*), vendors(name, contact_name, phone, email)')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false }),
        supabaseData.from('vendors')
          .select('*')
          .eq('company_id', companyId)
          .order('name'),
        supabaseData.from('inventory_items')
          .select('*, inventory_stock(*)')
          .eq('company_id', companyId)
          .order('name')
      ]);
      if (posRes.data) setPurchaseOrders(posRes.data);
      if (vendorsRes.data) setVendors(vendorsRes.data);
      if (invRes.data) setInventoryItems(invRes.data);
    } catch (err) {
      console.error('Error loading PO data:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Filtered POs
  const filteredPOs = purchaseOrders.filter(po => {
    if (statusFilter !== 'all' && po.status !== statusFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const vendorName = po.vendors?.name || '';
      return (
        (po.po_number || '').toLowerCase().includes(term) ||
        vendorName.toLowerCase().includes(term)
      );
    }
    return true;
  });

  // ─── Create / Edit ─────────────────────────────────────────
  function openCreateModal() {
    setEditingPO(null);
    setPOForm({ vendor_id: '', expected_date: '', notes: '', tax: 0 });
    setPOItems([]);
    setShowModal(true);
  }

  function openEditModal(po) {
    setEditingPO(po);
    setPOForm({
      vendor_id: po.vendor_id || '',
      expected_date: po.expected_date || '',
      notes: po.notes || '',
      tax: po.tax || 0
    });
    setPOItems((po.purchase_order_items || []).map(item => ({
      id: item.id,
      inventory_item_id: item.inventory_item_id || null,
      name: item.name || '',
      part_number: item.part_number || '',
      quantity: item.quantity || 1,
      unit_cost: item.unit_cost || 0,
      sort_order: item.sort_order || 0
    })));
    setShowModal(true);
  }

  function addManualItem() {
    setPOItems(prev => [...prev, {
      id: null,
      inventory_item_id: null,
      name: '',
      part_number: '',
      quantity: 1,
      unit_cost: 0,
      sort_order: prev.length
    }]);
  }

  function addInventoryItem(invItem) {
    setPOItems(prev => {
      const exists = prev.find(p => p.inventory_item_id === invItem.id);
      if (exists) return prev;
      return [...prev, {
        id: null,
        inventory_item_id: invItem.id,
        name: invItem.name || '',
        part_number: invItem.part_number || '',
        quantity: invItem.reorder_quantity || 1,
        unit_cost: invItem.unit_cost || 0,
        sort_order: prev.length
      }];
    });
    setShowItemPicker(false);
    setItemSearchTerm('');
  }

  async function addLowStockItems() {
    let added = 0;
    for (const item of inventoryItems) {
      const stock = (item.inventory_stock || []).reduce((sum, s) => sum + (s.quantity_on_hand || 0), 0);
      if (item.reorder_point != null && stock <= item.reorder_point) {
        const exists = poItems.find(p => p.inventory_item_id === item.id);
        if (!exists) {
          setPOItems(prev => [...prev, {
            id: null,
            inventory_item_id: item.id,
            name: item.name || '',
            part_number: item.part_number || '',
            quantity: item.reorder_quantity || 1,
            unit_cost: item.unit_cost || 0,
            sort_order: prev.length
          }]);
          added++;
        }
      }
    }
    if (added === 0) {
      await showError('No items are below their reorder point.');
    } else {
      await showSuccess(`Added ${added} low stock item${added > 1 ? 's' : ''}.`);
    }
  }

  function updateItem(index, field, value) {
    setPOItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      return { ...item, [field]: value };
    }));
  }

  function removeItem(index) {
    setPOItems(prev => prev.filter((_, i) => i !== index));
  }

  function getSubtotal() {
    return poItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0), 0);
  }

  async function savePO() {
    if (!poForm.vendor_id) { await showError('Please select a vendor.'); return; }
    if (poItems.length === 0) { await showError('Add at least one item.'); return; }

    setSaving(true);
    try {
      const subtotal = getSubtotal();
      const tax = parseFloat(poForm.tax) || 0;
      const total = subtotal + tax;

      if (editingPO) {
        // Update PO header
        const { error: poErr } = await supabaseData.from('purchase_orders')
          .update({
            vendor_id: poForm.vendor_id,
            expected_date: poForm.expected_date || null,
            notes: poForm.notes || null,
            subtotal,
            tax,
            total
          })
          .eq('id', editingPO.id);
        if (poErr) throw poErr;

        // Delete old items, insert new
        await supabaseData.from('purchase_order_items')
          .delete()
          .eq('purchase_order_id', editingPO.id);

        const itemRows = poItems.map((item, idx) => ({
          purchase_order_id: editingPO.id,
          inventory_item_id: item.inventory_item_id || null,
          name: item.name,
          part_number: item.part_number || null,
          quantity: parseFloat(item.quantity) || 1,
          unit_cost: parseFloat(item.unit_cost) || 0,
          total_cost: (parseFloat(item.quantity) || 1) * (parseFloat(item.unit_cost) || 0),
          received_quantity: 0,
          sort_order: idx
        }));
        const { error: itemErr } = await supabaseData.from('purchase_order_items').insert(itemRows);
        if (itemErr) throw itemErr;
      } else {
        // Create new PO
        const { data: newPO, error: poErr } = await supabaseData.from('purchase_orders')
          .insert({
            company_id: companyId,
            vendor_id: poForm.vendor_id,
            po_number: generatePONumber(),
            status: 'draft',
            subtotal,
            tax,
            total,
            notes: poForm.notes || null,
            expected_date: poForm.expected_date || null
          })
          .select()
          .single();
        if (poErr) throw poErr;

        const itemRows = poItems.map((item, idx) => ({
          purchase_order_id: newPO.id,
          inventory_item_id: item.inventory_item_id || null,
          name: item.name,
          part_number: item.part_number || null,
          quantity: parseFloat(item.quantity) || 1,
          unit_cost: parseFloat(item.unit_cost) || 0,
          total_cost: (parseFloat(item.quantity) || 1) * (parseFloat(item.unit_cost) || 0),
          received_quantity: 0,
          sort_order: idx
        }));
        const { error: itemErr } = await supabaseData.from('purchase_order_items').insert(itemRows);
        if (itemErr) throw itemErr;
      }

      setShowModal(false);
      await loadData();
      await showSuccess(editingPO ? 'Purchase order updated.' : 'Purchase order created.');
    } catch (err) {
      console.error('Error saving PO:', err);
      await showError('Failed to save purchase order: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }

  // ─── Status change ─────────────────────────────────────────
  async function markAsSent(po) {
    const ok = await showConfirm('Mark this PO as sent to vendor?', { title: 'Send PO', confirmText: 'Mark Sent' });
    if (!ok) return;
    const { error } = await supabaseData.from('purchase_orders')
      .update({ status: 'sent' })
      .eq('id', po.id);
    if (error) { await showError('Failed to update status.'); return; }
    await loadData();
  }

  async function cancelPO(po) {
    const ok = await showConfirm('Cancel this purchase order? This cannot be undone.', {
      title: 'Cancel PO',
      confirmText: 'Cancel PO',
      confirmStyle: 'danger'
    });
    if (!ok) return;
    const { error } = await supabaseData.from('purchase_orders')
      .update({ status: 'cancelled' })
      .eq('id', po.id);
    if (error) { await showError('Failed to cancel.'); return; }
    await loadData();
  }

  async function deletePO(po) {
    const ok = await showConfirm(`Delete ${po.po_number}? This permanently removes the PO and its items.`, {
      title: 'Delete PO',
      confirmText: 'Delete',
      confirmStyle: 'danger'
    });
    if (!ok) return;
    await supabaseData.from('purchase_order_items').delete().eq('purchase_order_id', po.id);
    const { error } = await supabaseData.from('purchase_orders').delete().eq('id', po.id);
    if (error) { await showError('Failed to delete.'); return; }
    await loadData();
  }

  // ─── Receive Flow ──────────────────────────────────────────
  function openReceiveModal(po) {
    setReceivingPO(po);
    setReceiveItems((po.purchase_order_items || []).map(item => ({
      id: item.id,
      inventory_item_id: item.inventory_item_id,
      name: item.name,
      part_number: item.part_number,
      quantity: item.quantity,
      received_quantity: item.received_quantity || 0,
      new_received: item.quantity - (item.received_quantity || 0)
    })));
    setShowReceiveModal(true);
  }

  async function confirmReceive() {
    if (!receivingPO) return;
    setReceiving(true);
    try {
      let allFullyReceived = true;

      for (const item of receiveItems) {
        const addQty = parseFloat(item.new_received) || 0;
        if (addQty <= 0) {
          if ((item.received_quantity || 0) < item.quantity) allFullyReceived = false;
          continue;
        }
        const totalReceived = (item.received_quantity || 0) + addQty;
        if (totalReceived < item.quantity) allFullyReceived = false;

        // Update PO item received_quantity
        await supabaseData.from('purchase_order_items')
          .update({ received_quantity: totalReceived })
          .eq('id', item.id);

        // Update inventory_stock if linked to inventory item
        if (item.inventory_item_id) {
          // Get default location
          const { data: defLoc } = await supabaseData.from('locations')
            .select('id').eq('company_id', companyId).eq('is_default', true).single();
          const locId = defLoc?.id;

          if (locId) {
            const { data: stockRows } = await supabaseData.from('inventory_stock')
              .select('*')
              .eq('inventory_item_id', item.inventory_item_id)
              .eq('location_id', locId)
              .limit(1);

            const prevQty = stockRows?.[0]?.quantity_on_hand || 0;
            const newQty = prevQty + addQty;

            if (stockRows && stockRows.length > 0) {
              await supabaseData.from('inventory_stock')
                .update({ quantity_on_hand: newQty, updated_at: new Date().toISOString() })
                .eq('id', stockRows[0].id);
            } else {
              await supabaseData.from('inventory_stock')
                .insert({
                  company_id: companyId,
                  inventory_item_id: item.inventory_item_id,
                  location_id: locId,
                  quantity_on_hand: addQty
                });
            }

            // Log transaction
            await supabaseData.from('inventory_transactions')
              .insert({
                inventory_item_id: item.inventory_item_id,
                company_id: companyId,
                location_id: locId,
                transaction_type: 'purchase',
                quantity_change: addQty,
                quantity_before: prevQty,
                quantity_after: newQty,
                notes: `Received from PO ${receivingPO.po_number}`
              });
          }
        }
      }

      const newStatus = allFullyReceived ? 'received' : 'partially_received';
      const updatePayload = { status: newStatus };
      if (allFullyReceived) updatePayload.received_date = new Date().toISOString().slice(0, 10);

      await supabaseData.from('purchase_orders')
        .update(updatePayload)
        .eq('id', receivingPO.id);

      setShowReceiveModal(false);
      await loadData();
      await showSuccess(allFullyReceived ? 'All items received. Inventory updated.' : 'Partial receipt recorded. Inventory updated.');
    } catch (err) {
      console.error('Receive error:', err);
      await showError('Failed to receive items: ' + (err.message || 'Unknown error'));
    } finally {
      setReceiving(false);
    }
  }

  // ─── Generate PDF ──────────────────────────────────────────
  async function generatePDF(po) {
    try {
      // Fetch company info
      const { data: company } = await supabaseData.from('companies')
        .select('name, address, city, state, zip, phone, email')
        .eq('id', companyId)
        .single();

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 20;

      // Company header
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(company?.name || 'Company', 14, y);
      y += 8;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const companyAddr = [company?.address, company?.city, company?.state, company?.zip].filter(Boolean).join(', ');
      if (companyAddr) { doc.text(companyAddr, 14, y); y += 5; }
      if (company?.phone) { doc.text(company.phone, 14, y); y += 5; }
      if (company?.email) { doc.text(company.email, 14, y); y += 5; }
      y += 4;

      // PO title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('PURCHASE ORDER', pageWidth - 14, 20, { align: 'right' });
      doc.setFontSize(11);
      doc.text(po.po_number || '', pageWidth - 14, 28, { align: 'right' });

      // Vendor info
      y += 2;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Vendor:', 14, y);
      doc.setFont('helvetica', 'normal');
      doc.text(po.vendors?.name || 'N/A', 40, y);
      y += 5;
      if (po.vendors?.contact_name) { doc.text('Contact: ' + po.vendors.contact_name, 14, y); y += 5; }
      if (po.vendors?.phone) { doc.text('Phone: ' + po.vendors.phone, 14, y); y += 5; }
      if (po.vendors?.email) { doc.text('Email: ' + po.vendors.email, 14, y); y += 5; }

      // Dates
      doc.setFont('helvetica', 'bold');
      doc.text('Date:', pageWidth - 80, y - 15);
      doc.setFont('helvetica', 'normal');
      doc.text(new Date(po.created_at).toLocaleDateString(), pageWidth - 60, y - 15);
      if (po.expected_date) {
        doc.setFont('helvetica', 'bold');
        doc.text('Expected:', pageWidth - 80, y - 10);
        doc.setFont('helvetica', 'normal');
        doc.text(formatDate(po.expected_date), pageWidth - 56, y - 10);
      }

      y += 6;

      // Items table header
      doc.setFillColor(240, 240, 240);
      doc.rect(14, y, pageWidth - 28, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('#', 16, y + 5.5);
      doc.text('Item', 24, y + 5.5);
      doc.text('Part #', 100, y + 5.5);
      doc.text('Qty', 135, y + 5.5);
      doc.text('Unit Cost', 152, y + 5.5);
      doc.text('Total', pageWidth - 16, y + 5.5, { align: 'right' });
      y += 12;

      // Items
      doc.setFont('helvetica', 'normal');
      const items = po.purchase_order_items || [];
      items.forEach((item, idx) => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.text(String(idx + 1), 16, y);
        doc.text(item.name || '', 24, y, { maxWidth: 72 });
        doc.text(item.part_number || '', 100, y, { maxWidth: 32 });
        doc.text(String(item.quantity || 0), 135, y);
        doc.text(formatCurrency(item.unit_cost), 152, y);
        doc.text(formatCurrency(item.total_cost), pageWidth - 16, y, { align: 'right' });
        y += 7;
      });

      // Totals
      y += 4;
      doc.setDrawColor(200);
      doc.line(120, y, pageWidth - 14, y);
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.text('Subtotal:', 135, y);
      doc.text(formatCurrency(po.subtotal), pageWidth - 16, y, { align: 'right' });
      y += 6;
      if (po.tax) {
        doc.text('Tax:', 135, y);
        doc.text(formatCurrency(po.tax), pageWidth - 16, y, { align: 'right' });
        y += 6;
      }
      doc.setFont('helvetica', 'bold');
      doc.text('Total:', 135, y);
      doc.text(formatCurrency(po.total), pageWidth - 16, y, { align: 'right' });

      // Notes
      if (po.notes) {
        y += 14;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text('Notes:', 14, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const lines = doc.splitTextToSize(po.notes, pageWidth - 28);
        doc.text(lines, 14, y);
      }

      doc.save(`${po.po_number || 'PurchaseOrder'}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      await showError('Failed to generate PDF.');
    }
  }

  // ─── Status badge ──────────────────────────────────────────
  function StatusBadge({ status }) {
    const sc = STATUS_COLORS[status] || STATUS_COLORS.draft;
    const label = status === 'partially_received' ? 'Partial' : (status || 'draft').charAt(0).toUpperCase() + (status || 'draft').slice(1);
    return (
      <span style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: sc.bg,
        color: sc.text,
        whiteSpace: 'nowrap'
      }}>{label}</span>
    );
  }

  // ─── Shared styles ─────────────────────────────────────────
  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.bgInput,
    color: colors.text,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box'
  };

  const btnPrimary = {
    padding: '10px 20px',
    borderRadius: 8,
    border: 'none',
    backgroundColor: colors.accent,
    color: '#fff',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  };

  const btnSecondary = {
    padding: '10px 20px',
    borderRadius: 8,
    border: `1px solid ${colors.border}`,
    backgroundColor: 'transparent',
    color: colors.text,
    fontWeight: 500,
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  };

  const btnSmall = {
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4
  };

  // ─── RENDER ────────────────────────────────────────────────

  // ── Item Picker Dropdown ───
  function renderItemPicker() {
    if (!showItemPicker) return null;
    const filtered = inventoryItems.filter(i => {
      if (!itemSearchTerm) return true;
      const t = itemSearchTerm.toLowerCase();
      return (i.name || '').toLowerCase().includes(t) || (i.part_number || '').toLowerCase().includes(t);
    });
    return (
      <div style={{
        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
        backgroundColor: colors.bgCard, border: `1px solid ${colors.border}`,
        borderRadius: 8, maxHeight: 250, overflowY: 'auto', boxShadow: colors.shadowLg
      }}>
        <div style={{ padding: 8 }}>
          <input
            style={inputStyle}
            placeholder="Search inventory items..."
            value={itemSearchTerm}
            onChange={e => setItemSearchTerm(e.target.value)}
            autoFocus
          />
        </div>
        {filtered.length === 0 && (
          <div style={{ padding: '12px 16px', color: colors.textMuted, fontSize: 13 }}>No items found</div>
        )}
        {filtered.map(item => {
          const stock = (item.inventory_stock || []).reduce((s, r) => s + (r.quantity_on_hand || 0), 0);
          return (
            <div
              key={item.id}
              onClick={() => addInventoryItem(item)}
              style={{
                padding: '10px 16px', cursor: 'pointer', borderTop: `1px solid ${colors.borderLight}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = colors.bgHover}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>{item.name}</div>
                {item.part_number && <div style={{ fontSize: 11, color: colors.textMuted }}>{item.part_number}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: colors.textSecondary }}>{formatCurrency(item.unit_cost)}</div>
                <div style={{ fontSize: 11, color: stock <= (item.reorder_point || 0) ? colors.error : colors.textMuted }}>
                  Stock: {stock}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── Create/Edit Modal ──────
  function renderModal() {
    if (!showModal) return null;
    const subtotal = getSubtotal();
    const tax = parseFloat(poForm.tax) || 0;
    const total = subtotal + tax;

    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: isMobile ? 0 : '40px 16px',
        overflowY: 'auto'
      }} onClick={() => setShowModal(false)}>
        <div style={{
          backgroundColor: colors.bgCard,
          borderRadius: isMobile ? 0 : 16,
          width: '100%',
          maxWidth: 720,
          minHeight: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : '90vh',
          overflowY: 'auto',
          boxShadow: colors.shadowLg
        }} onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            position: 'sticky', top: 0, backgroundColor: colors.bgCard, zIndex: 1
          }}>
            <h2 style={{ margin: 0, fontSize: 18, color: colors.text }}>
              {editingPO ? `Edit ${editingPO.po_number}` : 'New Purchase Order'}
            </h2>
            <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted }}>
              <X size={20} />
            </button>
          </div>

          <div style={{ padding: '20px 24px' }}>
            {/* Vendor & Date */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: colors.textSecondary }}>Vendor *</label>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={poForm.vendor_id}
                  onChange={e => setPOForm(f => ({ ...f, vendor_id: e.target.value }))}
                >
                  <option value="">Select vendor...</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: colors.textSecondary }}>Expected Delivery</label>
                <input
                  type="date"
                  style={inputStyle}
                  value={poForm.expected_date}
                  onChange={e => setPOForm(f => ({ ...f, expected_date: e.target.value }))}
                />
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: colors.textSecondary }}>Notes</label>
              <textarea
                style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                value={poForm.notes}
                onChange={e => setPOForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Internal notes..."
              />
            </div>

            {/* Items */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>Items</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button style={{ ...btnSmall, backgroundColor: colors.accent, color: '#fff' }} onClick={() => setShowItemPicker(p => !p)}>
                    <Package size={13} /> From Inventory
                  </button>
                  <button style={{ ...btnSmall, backgroundColor: colors.bgInput, color: colors.text, border: `1px solid ${colors.border}` }} onClick={addManualItem}>
                    <Plus size={13} /> Manual
                  </button>
                  <button style={{ ...btnSmall, backgroundColor: colors.warning + '22', color: colors.warning, border: `1px solid ${colors.warning}44` }} onClick={addLowStockItems}>
                    <AlertTriangle size={13} /> Add Low Stock
                  </button>
                </div>
              </div>

              {/* Item picker */}
              <div style={{ position: 'relative' }}>
                {renderItemPicker()}
              </div>

              {poItems.length === 0 && (
                <div style={{
                  padding: 24, textAlign: 'center', color: colors.textMuted, fontSize: 13,
                  border: `1px dashed ${colors.border}`, borderRadius: 8
                }}>
                  No items added yet. Add from inventory or manually.
                </div>
              )}

              {poItems.map((item, idx) => (
                <div key={idx} style={{
                  padding: isMobile ? '12px' : '12px 0',
                  borderBottom: `1px solid ${colors.borderLight}`,
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 70px 90px 80px 32px',
                  gap: isMobile ? 8 : 10,
                  alignItems: 'center'
                }}>
                  {isMobile && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600 }}>Item {idx + 1}</span>
                      <button onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.error }}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                  <input
                    style={{ ...inputStyle, padding: '8px 10px', fontSize: 13 }}
                    placeholder="Item name"
                    value={item.name}
                    onChange={e => updateItem(idx, 'name', e.target.value)}
                  />
                  <input
                    style={{ ...inputStyle, padding: '8px 10px', fontSize: 13 }}
                    placeholder="Part #"
                    value={item.part_number}
                    onChange={e => updateItem(idx, 'part_number', e.target.value)}
                  />
                  <input
                    type="number"
                    min="1"
                    style={{ ...inputStyle, padding: '8px 10px', fontSize: 13, textAlign: 'center' }}
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={e => updateItem(idx, 'quantity', e.target.value)}
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={{ ...inputStyle, padding: '8px 10px', fontSize: 13 }}
                    placeholder="Unit $"
                    value={item.unit_cost}
                    onChange={e => updateItem(idx, 'unit_cost', e.target.value)}
                  />
                  <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_cost) || 0))}
                  </div>
                  {!isMobile && (
                    <button onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.error, padding: 4 }}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Totals */}
            {poItems.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
                <div style={{ minWidth: 220 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, color: colors.textSecondary }}>
                    <span>Subtotal</span>
                    <span style={{ fontWeight: 600, color: colors.text }}>{formatCurrency(subtotal)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, fontSize: 13 }}>
                    <span style={{ color: colors.textSecondary }}>Tax</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      style={{ ...inputStyle, width: 90, padding: '4px 8px', fontSize: 13, textAlign: 'right' }}
                      value={poForm.tax}
                      onChange={e => setPOForm(f => ({ ...f, tax: e.target.value }))}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: `1px solid ${colors.border}`, fontSize: 15, fontWeight: 700, color: colors.text }}>
                    <span>Total</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '16px 24px',
            borderTop: `1px solid ${colors.border}`,
            display: 'flex', justifyContent: 'flex-end', gap: 12,
            position: 'sticky', bottom: 0, backgroundColor: colors.bgCard
          }}>
            <button style={btnSecondary} onClick={() => setShowModal(false)}>Cancel</button>
            <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={savePO} disabled={saving}>
              <Save size={16} /> {saving ? 'Saving...' : 'Save PO'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Receive Modal ──────────
  function renderReceiveModal() {
    if (!showReceiveModal || !receivingPO) return null;
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: isMobile ? 0 : '40px 16px',
        overflowY: 'auto'
      }} onClick={() => setShowReceiveModal(false)}>
        <div style={{
          backgroundColor: colors.bgCard,
          borderRadius: isMobile ? 0 : 16,
          width: '100%', maxWidth: 600,
          minHeight: isMobile ? '100vh' : 'auto',
          boxShadow: colors.shadowLg
        }} onClick={e => e.stopPropagation()}>
          <div style={{
            padding: '20px 24px', borderBottom: `1px solid ${colors.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <h2 style={{ margin: 0, fontSize: 18, color: colors.text }}>
              Receive {receivingPO.po_number}
            </h2>
            <button onClick={() => setShowReceiveModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted }}>
              <X size={20} />
            </button>
          </div>

          <div style={{ padding: '20px 24px' }}>
            <p style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 16 }}>
              Enter the quantity received for each item. Previously received quantities are shown.
            </p>

            {receiveItems.map((item, idx) => {
              const remaining = item.quantity - (item.received_quantity || 0);
              return (
                <div key={item.id} style={{
                  padding: '14px 0', borderBottom: `1px solid ${colors.borderLight}`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  flexWrap: 'wrap', gap: 8
                }}>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{item.name}</div>
                    {item.part_number && <div style={{ fontSize: 11, color: colors.textMuted }}>{item.part_number}</div>}
                    <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                      Ordered: {item.quantity} | Previously received: {item.received_quantity || 0} | Remaining: {remaining}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label style={{ fontSize: 12, color: colors.textSecondary }}>Receive:</label>
                    <input
                      type="number"
                      min="0"
                      max={remaining}
                      style={{ ...inputStyle, width: 80, padding: '8px 10px', fontSize: 14, textAlign: 'center' }}
                      value={item.new_received}
                      onChange={e => {
                        const val = e.target.value;
                        setReceiveItems(prev => prev.map((ri, i) => i === idx ? { ...ri, new_received: val } : ri));
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{
            padding: '16px 24px', borderTop: `1px solid ${colors.border}`,
            display: 'flex', justifyContent: 'flex-end', gap: 12
          }}>
            <button style={btnSecondary} onClick={() => setShowReceiveModal(false)}>Cancel</button>
            <button
              style={{ ...btnPrimary, backgroundColor: colors.success, opacity: receiving ? 0.6 : 1 }}
              onClick={confirmReceive}
              disabled={receiving}
            >
              <PackageCheck size={16} /> {receiving ? 'Processing...' : 'Confirm Receipt'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── PO Card ────────────────
  function renderPOCard(po) {
    const items = po.purchase_order_items || [];
    const vendor = po.vendors;
    const canEdit = po.status === 'draft';
    const canSend = po.status === 'draft';
    const canReceive = po.status === 'sent' || po.status === 'partially_received';
    const canCancel = po.status !== 'cancelled' && po.status !== 'received';

    return (
      <div
        key={po.id}
        style={{
          backgroundColor: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 12,
          padding: isMobile ? 14 : 20,
          cursor: canEdit ? 'pointer' : 'default',
          transition: 'border-color 0.15s'
        }}
        onClick={() => canEdit && openEditModal(po)}
        onMouseEnter={e => { if (canEdit) e.currentTarget.style.borderColor = colors.accent; }}
        onMouseLeave={e => e.currentTarget.style.borderColor = colors.border}
      >
        {/* Top row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>{po.po_number}</div>
            <div style={{ fontSize: 13, color: colors.textSecondary, marginTop: 2 }}>
              <Truck size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {vendor?.name || 'No vendor'}
            </div>
          </div>
          <StatusBadge status={po.status} />
        </div>

        {/* Details row */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: 12, color: colors.textMuted, marginBottom: 12, flexWrap: 'wrap', gap: 6
        }}>
          <span><Package size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {items.length} item{items.length !== 1 ? 's' : ''}</span>
          <span style={{ fontWeight: 700, fontSize: 14, color: colors.text }}>{formatCurrency(po.total)}</span>
        </div>

        {po.expected_date && (
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>
            <Calendar size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Expected: {formatDate(po.expected_date)}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
          {canEdit && (
            <button style={{ ...btnSmall, backgroundColor: colors.bgInput, color: colors.text, border: `1px solid ${colors.border}` }} onClick={() => openEditModal(po)}>
              <Edit2 size={12} /> Edit
            </button>
          )}
          {canSend && (
            <button style={{ ...btnSmall, backgroundColor: colors.accent, color: '#fff' }} onClick={() => markAsSent(po)}>
              <Truck size={12} /> Mark Sent
            </button>
          )}
          {canReceive && (
            <button style={{ ...btnSmall, backgroundColor: colors.success, color: '#fff' }} onClick={() => openReceiveModal(po)}>
              <PackageCheck size={12} /> Receive
            </button>
          )}
          <button style={{ ...btnSmall, backgroundColor: colors.bgInput, color: colors.text, border: `1px solid ${colors.border}` }} onClick={() => generatePDF(po)}>
            <Download size={12} /> PDF
          </button>
          {canCancel && (
            <button style={{ ...btnSmall, backgroundColor: colors.error + '18', color: colors.error, border: `1px solid ${colors.error}33` }} onClick={() => cancelPO(po)}>
              <X size={12} /> Cancel
            </button>
          )}
          {po.status === 'draft' && (
            <button style={{ ...btnSmall, backgroundColor: colors.error + '18', color: colors.error, border: `1px solid ${colors.error}33` }} onClick={() => deletePO(po)}>
              <Trash2 size={12} /> Delete
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Main Render ────────────
  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem 3rem', minHeight: '100vh' }}>
      {/* Page Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 12 : 0,
        marginBottom: 24
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: isMobile ? 40 : 48, height: isMobile ? 40 : 48,
            borderRadius: 12, background: colors.accentGradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <ClipboardList size={isMobile ? 20 : 24} color="#fff" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: isMobile ? 20 : 28, fontWeight: 800, color: colors.text }}>Purchase Orders</h1>
            <p style={{ margin: 0, fontSize: 13, color: colors.textSecondary }}>
              {purchaseOrders.length} total order{purchaseOrders.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <button style={btnPrimary} onClick={openCreateModal}>
          <Plus size={18} /> New PO
        </button>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: isMobile ? '1 1 100%' : '0 1 280px' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: colors.textMuted }} />
          <input
            style={{ ...inputStyle, paddingLeft: 36 }}
            placeholder="Search PO # or vendor..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Status filter pills */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              style={{
                padding: '7px 14px',
                borderRadius: 20,
                border: statusFilter === opt.value ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                backgroundColor: statusFilter === opt.value ? colors.accent + '18' : 'transparent',
                color: statusFilter === opt.value ? colors.accent : colors.textSecondary,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* PO List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: colors.textMuted }}>
          Loading purchase orders...
        </div>
      ) : filteredPOs.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 60,
          border: `1px dashed ${colors.border}`, borderRadius: 12
        }}>
          <ClipboardList size={48} color={colors.textMuted} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 600, color: colors.textSecondary, marginBottom: 6 }}>
            {searchTerm || statusFilter !== 'all' ? 'No matching purchase orders' : 'No purchase orders yet'}
          </div>
          <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your filters.'
              : 'Create your first purchase order to start tracking inventory purchases.'}
          </div>
          {!searchTerm && statusFilter === 'all' && (
            <button style={btnPrimary} onClick={openCreateModal}>
              <Plus size={16} /> Create Purchase Order
            </button>
          )}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(360px, 1fr))',
          gap: 16
        }}>
          {filteredPOs.map(po => renderPOCard(po))}
        </div>
      )}

      {/* Modals */}
      {renderModal()}
      {renderReceiveModal()}
    </div>
  );
}
